// Google Docs API service — OAuth2 + Offline-First
//
// All API responses are cached in IndexedDB so the app continues to work
// when offline. Cached data is served immediately; background refresh
// happens when online.
//
// API docs: https://developers.google.com/docs/api
// Drive API: https://developers.google.com/drive/api/v3

import { getSetting, setSetting } from "./storage";
import type { GoogleDoc } from "./types";

// ---------------------------------------------------------------------------
// Cache keys
// ---------------------------------------------------------------------------

const CACHE_KEY_ACTIVITY = "google-docs-cache-activity";
const CACHE_TTL_MS = 30 * 60 * 1000; // 30 minutes

interface CacheEntry<T> {
  data: T;
  cachedAt: number;
  userId: string;
}

// ---------------------------------------------------------------------------
// GoogleDocsService
// ---------------------------------------------------------------------------

export class GoogleDocsService {
  private accessToken: string;
  private userId: string;

  constructor(accessToken: string, userId?: string) {
    this.accessToken = accessToken;
    this.userId = userId || 'default';
  }

  // ---------------------------------------------------------------------------
  // HTTP helper — attaches OAuth token and handles offline gracefully
  // ---------------------------------------------------------------------------

  private async request<T>(url: string, options: RequestInit = {}): Promise<T> {
    console.log(`[GoogleDocs] ${options.method ?? "GET"} ${url}`);

    const response = await fetch(url, {
      ...options,
      headers: {
        Authorization: `Bearer ${this.accessToken}`,
        Accept: "application/json",
        "Content-Type": "application/json",
        ...((options.headers as Record<string, string>) ?? {}),
      },
    });

    if (!response.ok) {
      const text = await response.text().catch(() => "");
      console.error(`[GoogleDocs] ${response.status} ${url}: ${text.slice(0, 200)}`);
      throw new Error(`Google API error ${response.status}: ${text || response.statusText}`);
    }

    return response.json() as Promise<T>;
  }

  // ---------------------------------------------------------------------------
  // Cache helpers
  // ---------------------------------------------------------------------------

  private async getCached<T>(key: string): Promise<T | null> {
    const entry = await getSetting<CacheEntry<T>>(key);
    if (!entry) return null;
    if (Date.now() - entry.cachedAt > CACHE_TTL_MS) return null;
    if (entry.userId !== this.userId) return null;
    return entry.data;
  }

  private async setCached<T>(key: string, data: T): Promise<void> {
    await setSetting<CacheEntry<T>>(key, { data, cachedAt: Date.now(), userId: this.userId });
  }

  /**
   * Offline-first fetch: return cached data immediately, refresh in background if online.
   * If no cache and offline: throws with a clear offline message.
   */
  private async fetchWithCache<T>(
    cacheKey: string,
    fetchFn: () => Promise<T>
  ): Promise<{ data: T; fromCache: boolean }> {
    const cached = await this.getCached<T>(cacheKey);
    if (cached) {
      // Background refresh if online
      if (navigator.onLine) {
        fetchFn()
          .then((fresh) => this.setCached(cacheKey, fresh))
          .catch((e) => console.warn("[GoogleDocs] Background refresh failed:", e));
      }
      return { data: cached, fromCache: true };
    }

    // No cache: must fetch
    const fresh = await fetchFn();
    await this.setCached(cacheKey, fresh);
    return { data: fresh, fromCache: false };
  }

  // ---------------------------------------------------------------------------
  // Public API methods
  // ---------------------------------------------------------------------------

  /**
   * Get documents and activities within a date range
   */
  async getEvents(from: Date, to: Date, folderId?: string): Promise<{ events: GoogleDoc[] }> {
    const cacheKey = `${CACHE_KEY_ACTIVITY}-${from.toISOString()}-${to.toISOString()}-${folderId || 'all'}`;
    const { data } = await this.fetchWithCache(cacheKey, async () => {
      const results: GoogleDoc[] = [];
      let pageToken: string | undefined;

      
      // Build query for Google Drive API
      let query = `trashed=false and (mimeType='application/vnd.google-apps.document' or mimeType='application/vnd.google-apps.spreadsheet' or mimeType='application/vnd.google-apps.presentation')`;
      
      if (folderId) {
        query += ` and '${folderId}' in parents`;
      }

      do {
        const url = `https://www.googleapis.com/drive/v3/files?q=${encodeURIComponent(query)}&fields=nextPageToken,files(id,name,mimeType,createdTime,modifiedTime,lastModifyingUser,webViewLink,parents)&pageSize=100${pageToken ? `&pageToken=${pageToken}` : ''}`;
        
        const response = await this.request<{ files: any[]; nextPageToken?: string }>(url);

        results.push(...response.files.map((file: any) => ({
          id: file.id,
          title: file.name,
          url: file.webViewLink,
          mimeType: file.mimeType,
          createdTime: file.createdTime,
          modifiedTime: file.modifiedTime,
          lastModifyingUser: file.lastModifyingUser ? {
            emailAddress: file.lastModifyingUser.emailAddress,
            displayName: file.lastModifyingUser.displayName || file.lastModifyingUser.emailAddress,
            photoLink: file.lastModifyingUser.photoLink
          } : undefined,
          parents: file.parents || [],
          starred: false,
          trashed: false
        })));

        pageToken = response.nextPageToken;
      } while (pageToken);

      // Filter by date range (Drive API doesn't support date filtering in query for modifications)
      const filtered = results.filter(doc => {
        const modifiedDate = new Date(doc.modifiedTime);
        return modifiedDate >= from && modifiedDate <= to;
      });

      // Get revision history for more detailed activity
      const eventsWithHistory = await Promise.all(
        filtered.map(async (doc) => {
          try {
            // Get revisions to find creation and major edits
            const revisionsUrl = `https://www.googleapis.com/drive/v3/files/${doc.id}/revisions?fields=revisions(id,modifiedTime,lastModifyingUser)&pageSize=10`;
            const revisions = await this.request<{ revisions: any[] }>(revisionsUrl);
            
            // Determine event type based on revisions
            const eventType = revisions.revisions.length > 1 ? 'updated' : 'created';
            const latestRevision = revisions.revisions[revisions.revisions.length - 1];
            
            return {
              ...doc,
              eventType,
              authorName: doc.lastModifyingUser?.displayName || 'Unknown',
              authorEmail: doc.lastModifyingUser?.emailAddress,
              timestamp: latestRevision?.modifiedTime || doc.modifiedTime
            };
          } catch (e) {
            // Fallback if revisions can't be fetched
            return {
              ...doc,
              eventType: 'updated' as const,
              authorName: doc.lastModifyingUser?.displayName || 'Unknown',
              authorEmail: doc.lastModifyingUser?.emailAddress,
              timestamp: doc.modifiedTime
            };
          }
        })
      );

      return { events: eventsWithHistory as GoogleDoc[] };
    });

    return data;
  }

  /**
   * Get specific document details
   */
  async getDoc(docId: string): Promise<GoogleDoc> {
    const url = `https://www.googleapis.com/drive/v3/files/${docId}?fields=id,name,mimeType,createdTime,modifiedTime,lastModifyingUser,webViewLink,parents`;
    const file = await this.request<any>(url);

    return {
      id: file.id,
      title: file.name,
      url: file.webViewLink,
      mimeType: file.mimeType,
      createdTime: file.createdTime,
      modifiedTime: file.modifiedTime,
      lastModifyingUser: file.lastModifyingUser ? {
        emailAddress: file.lastModifyingUser.emailAddress,
        displayName: file.lastModifyingUser.displayName || file.lastModifyingUser.emailAddress,
        photoLink: file.lastModifyingUser.photoLink
      } : undefined,
      parents: file.parents || [],
      starred: false,
      trashed: false
    };
  }

  /**
   * Get document comments
   */
  async getComments(docId: string): Promise<any[]> {
    try {
      const url = `https://www.googleapis.com/drive/v3/files/${docId}/comments?fields=comments(id,content,createdTime,modifiedTime,author,replies)&pageSize=100`;
      const response = await this.request<{ comments: any[] }>(url);
      return response.comments || [];
    } catch (e) {
      console.warn(`[GoogleDocs] Could not fetch comments for ${docId}:`, e);
      return [];
    }
  }
}

// ---------------------------------------------------------------------------
// Factory function
// ---------------------------------------------------------------------------

export async function createGoogleDocsService(): Promise<GoogleDocsService | null> {
  const token = await getSetting<string>('googleDocsOAuthToken');
  if (!token) {
    console.warn("[GoogleDocs] No OAuth token configured");
    return null;
  }

  try {
    // Test the token by fetching user info
    const response = await fetch('https://www.googleapis.com/oauth2/v2/userinfo', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      console.error("[GoogleDocs] Invalid OAuth token");
      return null;
    }

    const user = await response.json();
    console.log(`[GoogleDocs] Connected as: ${user.name || user.email}`);

    return new GoogleDocsService(token, user.id);
  } catch (e) {
    console.error("[GoogleDocs] Failed to initialize service:", e);
    return null;
  }
}
